
import { useContext } from "react";
import { CenterContext, ClassesContext, StaffContext, StudentsContext, UserContext } from "../../../Context";


/**
 * Custom hook for accessing multiple context values.
 * @returns {Object} An object containing various context values and functions:
 * @property {Object} center - Current center data from CenterContext
 * @property {Array} locations - Array of locations from CenterContext
 * @property {Array} staff - Array of staff members from StaffContext
 * @property {Function} findStaff - Function to find staff members from StaffContext
 * @property {Function} getStaffRole - Function to get staff role from StaffContext
 * @property {Array} classes - Array of classes from ClassesContext
 * * @property {Function} findClass - Function to find a class from ClassesContext
 * @property {Function} findClasses - Function to find classes from ClassesContext
 * @property {Array} activeClasses - Array of active classes from ClassesContext
 * @property {Array} classDays - Array of class days from ClassesContext given a class ID
 * @property {Array} teachersClasses - Array of teacher's classes from ClassesContext given a staff ID
 * @property {Array} todaysClasses - Array of today's classes from ClassesContext
 * @property {Object} user - Current user data from UserContext
 */

type UseCTXType = {
  locations: Location[];
  findClass: (id: string) => HtClass;
  user: userType;
  center: center;
  staff: userType[];
  findStaff: (id: string) => userType;
  getStaffRole: (id: string) => userType[];
  classes: HtClass[];
  findClasses: (id: string[]) => HtClass[];
  activeClasses: () => HtClass[],
  classDays: (id: string) => classDay[],
  teachersClasses: (teacher: string) => HtClass[],
  todaysClasses: () => HtClass[],
  findStudent: (id: string) => Student,
};

type Student = {
  _id: string,
  name: string,
  phone: string,
  status: string,
  classes: string[],
  customer: string,
  DateOfBirth: string,
  doesntStudy: [string],
  image: string,
  statusChanges: string,
  lessonsToStudy: number,
}

type Location = {
    _id: string,
    name: string,
}

type HtClass = {
    _id: string,
    name: string,
    classDays: classDay[]
    location: string,
}

type classDay = {
    _id: string,
    day: number,
    startTime: string,
    endTime: string
}

type userType = {
  _id: string,
  name: string,
  roles: string[],
  status: string,
  dayOff: number,
  center: string,
  reportsTo: string,
}

type center = {
  _id:string
}

type ActiveClasses = {}



export default function useCTX(): UseCTXType {
  const { center, locations } = useContext(CenterContext);
  const {
    classes,
    findClasses,
    activeClasses,
    classDays,
    teachersClasses,
    todaysClasses,
    findClass,
  } = useContext(ClassesContext);
  const { staff, findStaff, getStaffRole } = useContext(StaffContext);
  const { user } = useContext(UserContext);
  const { findStudent } = useContext(StudentsContext);
  return {
    center,
    locations,
    staff,
    findStaff,
    getStaffRole,
    classes,
    findClass,
    findClasses,
    activeClasses,
    classDays,
    teachersClasses,
    todaysClasses,
    user,
    findStudent,
  };
}